import { cloneElement } from "react";

import type { IconComponent } from "@/components/Icon/Icon";
import { twResolve } from "@/helpers/tw-resolve";

type NoticeProps = {
  icon?: IconComponent;
  "data-testid"?: string;
  type?: "info" | "error" | "warning" | "positive";
  action?: {
    label: string;
    onClick: () => void;
  };
  styling?: "outlined" | "filled";
} & (
  | {
      message: string;
      customBody?: never;
    }
  | {
      message?: never;
      customBody: React.ReactElement;
    }
);

export function Notice({
  icon,
  message,
  customBody,
  type = "info",
  action,
  styling = "outlined",
  "data-testid": dataTestId,
}: NoticeProps): React.ReactNode {
  return (
    <div
      className={twResolve(
        "flex w-full items-center gap-4 rounded-lg p-4",
        styling === "outlined" && "border",
        type === "info" && "border-blue-600 bg-blue-100 [&>*]:text-blue-600",
        type === "error" && "border-red-600 bg-red-100 [&>*]:text-red-600",
        type === "positive" && "border-green-600 bg-green-100 [&>*]:text-green-600",
        type === "warning" && "border-yellow-600 bg-yellow-100 [&>*]:text-yellow-600",
      )}
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-2">
        {icon && cloneElement(icon, { size: 24 })}
        {message && <p className="text-caption-bold">{message}</p>}
        {customBody}
      </div>
      {action && (
        <button type="button" onClick={action.onClick} className="ml-auto min-h-6 shrink-0 text-caption-bold underline">
          {action.label}
        </button>
      )}
    </div>
  );
}
